import { z } from 'zod';

export const bankImportLCCaseStatusSchema = z.enum([
  'intake_processing', 'intake_failed', 'extraction_review', 'draft',
  'validation_blocked', 'checker_review', 'returned_to_maker',
  'authorizer_review', 'approved', 'handoff_pending', 'handed_off',
  'handoff_failed', 'accepted_by_bank_system', 'handoff_rejected',
]);
export type BankImportLCCaseStatus = z.infer<typeof bankImportLCCaseStatusSchema>;

export const decimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/);
export type DecimalString = z.infer<typeof decimalStringSchema>;

const uuidSchema = z.string().uuid();
const timestampSchema = z.string().datetime({ offset: true });
const nonEmptyString = z.string().min(1);

type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };

const isJsonValue = (value: unknown): value is JsonValue => {
  if (
    value === null
    || typeof value === 'string'
    || typeof value === 'boolean'
  ) {
    return true;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value);
  }
  if (Array.isArray(value)) {
    return value.every(isJsonValue);
  }
  if (typeof value === 'object') {
    const prototype = Object.getPrototypeOf(value);
    return (
      (prototype === Object.prototype || prototype === null)
      && Object.values(value).every(isJsonValue)
    );
  }
  return false;
};

const jsonValueSchema = z.custom<JsonValue>(
  isJsonValue,
  'Expected a JSON value',
);

export const bankImportLCRoleSchema = z.enum([
  'maker',
  'checker',
  'authorizer',
  'operations_admin',
  'integration_principal',
]);
export type BankImportLCRole = z.infer<typeof bankImportLCRoleSchema>;

export const bankImportLCRoleGrantSchema = z.object({
  role: bankImportLCRoleSchema,
  branch_id: uuidSchema.nullable(),
}).strict();
export type BankImportLCRoleGrant = z.infer<typeof bankImportLCRoleGrantSchema>;

export const bankImportLCActorPrincipalSchema = z.object({
  actor_id: uuidSchema,
  bank_id: uuidSchema,
  branch_ids: z.array(uuidSchema),
  roles: z.array(bankImportLCRoleSchema),
  role_grants: z.array(bankImportLCRoleGrantSchema),
  correlation_id: nonEmptyString,
  all_branches: z.boolean(),
}).strict();
export type BankImportLCActorPrincipal = z.infer<
  typeof bankImportLCActorPrincipalSchema
>;

export const bankImportLCMoneySchema = z.object({
  amount: decimalStringSchema,
  currency: z.string().length(3),
}).strict();
export type BankImportLCMoney = z.infer<typeof bankImportLCMoneySchema>;

export const bankImportLCPartySchema = z.object({
  name: nonEmptyString,
  address: nonEmptyString,
}).strict();
export type BankImportLCParty = z.infer<typeof bankImportLCPartySchema>;

export const bankImportLCContentSchema = z.object({
  applicationReference: nonEmptyString,
  currency: z.string().length(3),
  amount: bankImportLCMoneySchema,
  applicant: bankImportLCPartySchema,
  beneficiary: bankImportLCPartySchema,
  goodsDescription: nonEmptyString,
  additionalConditions: z.array(nonEmptyString),
  policyAttributes: z.record(z.unknown()),
}).strict();
export type BankImportLCContent = z.infer<typeof bankImportLCContentSchema>;

export const bankImportLCExtractionMoneySchema = z.object({
  amount: decimalStringSchema.nullable(),
  currency: z.string().length(3).nullable(),
}).strict();

export const bankImportLCExtractionPartySchema = z.object({
  name: nonEmptyString.nullable(),
  identifier: nonEmptyString.nullable(),
}).strict();

export const bankImportLCExtractionFieldsSchema = z.object({
  applicationReference: nonEmptyString.nullable(),
  amount: bankImportLCExtractionMoneySchema.nullable(),
  applicant: bankImportLCExtractionPartySchema.nullable(),
  beneficiary: bankImportLCExtractionPartySchema.nullable(),
  expiryDate: nonEmptyString.nullable(),
  incoterms: nonEmptyString.nullable(),
  portOfLoading: nonEmptyString.nullable(),
  portOfDischarge: nonEmptyString.nullable(),
  countryOfOrigin: nonEmptyString.nullable(),
}).strict();

export const bankImportLCExtractionResolutionSchema = z.object({
  fieldPath: nonEmptyString,
  reason: nonEmptyString,
  candidateCount: z.number().int().nonnegative(),
  requiresOfficerReview: z.boolean(),
  expected: nonEmptyString,
  found: nonEmptyString,
  suggestedFix: nonEmptyString,
}).strict();

export const bankImportLCExtractionSourceResultSchema = z.object({
  sourceDocumentId: uuidSchema,
  documentId: uuidSchema,
  documentType: nonEmptyString,
  status: z.enum(['extracted', 'terminal_failed']),
  failureCode: nonEmptyString.nullable(),
  candidateCount: z.number().int().nonnegative(),
}).strict();

export const bankImportLCExtractionDraftSchema = z.object({
  kind: z.literal('bank_import_lc_extraction_draft'),
  schemaVersion: z.literal('1.0'),
  fields: bankImportLCExtractionFieldsSchema,
  rawValues: z.record(z.string()),
  resolutionQueue: z.array(bankImportLCExtractionResolutionSchema),
  sources: z.array(bankImportLCExtractionSourceResultSchema),
  authority: z.literal('deterministic_or_officer_review'),
}).strict();
export type BankImportLCExtractionDraft = z.infer<
  typeof bankImportLCExtractionDraftSchema
>;

export const bankImportLCVersionSchema = z.object({
  id: uuidSchema,
  caseId: uuidSchema,
  versionNumber: z.number().int().positive(),
  content: z.union([
    bankImportLCExtractionDraftSchema,
    bankImportLCContentSchema,
  ]),
  payloadHash: nonEmptyString,
  parentVersionId: uuidSchema.nullable(),
  changeSummary: nonEmptyString,
  createdBy: uuidSchema,
  reason: nonEmptyString,
  validationSnapshotId: uuidSchema.nullable(),
  approvalStatus: z.enum(['pending', 'checker_approved', 'approved', 'returned', 'rejected']),
  createdAt: timestampSchema,
}).strict();
export type BankImportLCVersion = z.infer<typeof bankImportLCVersionSchema>;

export const bankImportLCSourceDocumentSchema = z.object({
  id: uuidSchema,
  caseId: uuidSchema,
  documentType: nonEmptyString,
  filename: nonEmptyString,
  contentHash: nonEmptyString,
  storageReference: nonEmptyString,
  sourceChannel: nonEmptyString,
  pageCount: z.number().int().positive(),
  mediaType: nonEmptyString,
  extractionStatus: z.enum(['pending', 'processing', 'completed', 'failed']),
  retentionClass: nonEmptyString,
  createdAt: timestampSchema,
}).strict();
export type BankImportLCSourceDocument = z.infer<typeof bankImportLCSourceDocumentSchema>;

export const bankImportLCDocumentEvidenceSourceSchema = z.object({
  kind: z.literal('document'),
  sourceDocumentId: uuidSchema,
  documentId: uuidSchema,
  page: z.number().int().positive(),
  boundingRegion: z.string().nullable(),
}).strict();

export const bankImportLCBankSystemEvidenceSourceSchema = z.object({
  kind: z.literal('bank_system'),
  sourceSystem: nonEmptyString,
  sourceRecordId: nonEmptyString,
  sourceVersion: nonEmptyString,
  retrievedAt: timestampSchema,
}).strict();

export const bankImportLCEvidenceSourceSchema = z.discriminatedUnion('kind', [
  bankImportLCDocumentEvidenceSourceSchema,
  bankImportLCBankSystemEvidenceSourceSchema,
]);

export const bankImportLCFieldEvidenceSchema = z.object({
  id: uuidSchema,
  versionId: uuidSchema,
  fieldPath: nonEmptyString,
  source: bankImportLCEvidenceSourceSchema,
  sourceText: nonEmptyString,
  extractionMethod: nonEmptyString,
  confidence: decimalStringSchema,
  extractedValue: nonEmptyString,
  currentValue: nonEmptyString,
  correctedBy: uuidSchema.nullable(),
  correctedAt: timestampSchema.nullable(),
  correctionReason: z.string().nullable(),
}).strict();
export type BankImportLCFieldEvidence = z.infer<typeof bankImportLCFieldEvidenceSchema>;

export const bankImportLCValidationRunSchema = z.object({
  id: uuidSchema,
  versionId: uuidSchema,
  rulesetDomain: nonEmptyString,
  rulesetVersion: nonEmptyString,
  status: z.enum(['pending', 'running', 'completed', 'failed']),
  startedAt: timestampSchema,
  completedAt: timestampSchema.nullable(),
}).strict();
export type BankImportLCValidationRun = z.infer<typeof bankImportLCValidationRunSchema>;

export const bankImportLCFindingSchema = z.object({
  id: z.string().uuid(),
  rule: z.string().min(1),
  rulesetDomain: z.string().min(1),
  rulesetVersion: z.string().min(1),
  findingClass: z.enum([
    'lc_discrepancy', 'sanctions_alert', 'tbml_alert',
    'policy_warning', 'message_conformance',
  ]),
  title: z.string().min(1),
  severity: z.enum(['critical', 'major', 'minor', 'advisory']),
  expected: z.string().min(1),
  found: z.string().min(1),
  suggestedFix: z.string().min(1),
  blocking: z.boolean(),
  documentIds: z.array(z.string().uuid()),
  evidenceIds: z.array(z.string().uuid()),
  disposition: z.enum(['open', 'accepted', 'overridden', 'resolved']),
}).strict();
export type BankImportLCFinding = z.infer<typeof bankImportLCFindingSchema>;

export const bankImportLCApprovalDecisionSchema = z.object({
  id: uuidSchema,
  versionId: uuidSchema,
  versionHash: nonEmptyString,
  stage: z.enum(['maker', 'checker', 'authorizer']),
  actorId: uuidSchema,
  role: nonEmptyString,
  decision: z.enum(['submitted', 'approved', 'returned', 'rejected']),
  reason: nonEmptyString,
  decidedAt: timestampSchema,
  previousStatus: bankImportLCCaseStatusSchema,
  resultingStatus: bankImportLCCaseStatusSchema,
}).strict();
export type BankImportLCApprovalDecision = z.infer<typeof bankImportLCApprovalDecisionSchema>;

export const bankImportLCMessageArtifactSchema = z.object({
  id: uuidSchema,
  versionId: uuidSchema,
  approvedVersionHash: nonEmptyString,
  messageType: z.enum(['MT700', 'MT701', 'MT707', 'examination_sheet', 'canonical_json']),
  standardsVersion: nonEmptyString,
  fieldMap: z.record(nonEmptyString),
  renderedContent: nonEmptyString,
  artifactHash: nonEmptyString,
  compilerVersion: nonEmptyString,
  validationStatus: z.enum(['valid', 'invalid']),
  createdAt: timestampSchema,
}).strict();
export type BankImportLCMessageArtifact = z.infer<typeof bankImportLCMessageArtifactSchema>;

export const bankImportLCHandoffAttemptSchema = z.object({
  id: uuidSchema,
  artifactId: uuidSchema,
  idempotencyKey: nonEmptyString,
  adapter: nonEmptyString,
  destination: nonEmptyString,
  attemptNumber: z.number().int().positive(),
  status: z.enum(['not_ready', 'ready_for_handoff', 'handoff_pending', 'delivered_to_bank_system', 'accepted_by_bank_system', 'rejected_by_bank_system', 'reconciliation_required']),
  requestReference: z.string().nullable(),
  responseReference: z.string().nullable(),
  attemptedAt: timestampSchema,
  reconciledAt: timestampSchema.nullable(),
}).strict();
export type BankImportLCHandoffAttempt = z.infer<typeof bankImportLCHandoffAttemptSchema>;

export const bankImportLCAuditEventSchema = z.object({
  id: uuidSchema,
  actorId: uuidSchema.nullable(),
  servicePrincipal: z.string().nullable(),
  bankId: uuidSchema,
  organizationId: uuidSchema,
  caseId: uuidSchema,
  versionId: uuidSchema.nullable(),
  artifactId: uuidSchema.nullable(),
  action: nonEmptyString,
  reason: z.string().nullable(),
  result: nonEmptyString,
  beforeHash: z.string().nullable(),
  afterHash: z.string().nullable(),
  correlationId: uuidSchema,
  occurredAt: timestampSchema,
  integrityLink: nonEmptyString,
}).strict();
export type BankImportLCAuditEvent = z.infer<typeof bankImportLCAuditEventSchema>;

const bankImportLCCaseSummaryFields = {
  id: uuidSchema,
  caseReference: nonEmptyString,
  bankId: uuidSchema,
  organizationId: uuidSchema,
  branchId: uuidSchema,
  customerReference: nonEmptyString,
  accountReference: nonEmptyString,
  title: nonEmptyString,
  status: bankImportLCCaseStatusSchema,
  priority: z.enum(['low', 'normal', 'high', 'urgent']),
  currentVersion: bankImportLCVersionSchema,
  approvedVersion: bankImportLCVersionSchema.nullable(),
  assignedOfficerIds: z.array(uuidSchema),
  slaDueAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
  closedAt: timestampSchema.nullable(),
};

export const bankImportLCCaseSummarySchema = z.object(bankImportLCCaseSummaryFields).strict();
export type BankImportLCCaseSummary = z.infer<typeof bankImportLCCaseSummarySchema>;

export const bankImportLCCaseDetailSchema = z.object({
  ...bankImportLCCaseSummaryFields,
  sourceDocuments: z.array(bankImportLCSourceDocumentSchema),
  fieldEvidence: z.array(bankImportLCFieldEvidenceSchema),
  validationRuns: z.array(bankImportLCValidationRunSchema),
  findings: z.array(bankImportLCFindingSchema),
  approvalDecisions: z.array(bankImportLCApprovalDecisionSchema),
  messageArtifacts: z.array(bankImportLCMessageArtifactSchema),
  handoffAttempts: z.array(bankImportLCHandoffAttemptSchema),
  auditEvents: z.array(bankImportLCAuditEventSchema),
}).strict();
export type BankImportLCCaseDetail = z.infer<typeof bankImportLCCaseDetailSchema>;

export const bankImportLCCaseListFiltersSchema = z.object({
  status: bankImportLCCaseStatusSchema.optional(),
  assignedOfficerId: uuidSchema.optional(),
  bankId: uuidSchema.optional(),
  branchId: uuidSchema.optional(),
  cursor: z.string().min(1).optional(),
  limit: z.number().int().min(1).max(100).default(25),
}).strict();
export type BankImportLCCaseListFilters = z.infer<typeof bankImportLCCaseListFiltersSchema>;

export const bankImportLCCursorPageSchema = z.object({
  items: z.array(bankImportLCCaseSummarySchema),
  nextCursor: z.string().min(1).nullable(),
}).strict();
export type BankImportLCCursorPage = z.infer<typeof bankImportLCCursorPageSchema>;

export const bankImportLCCreateRequestSchema = z.object({
  externalCaseRef: nonEmptyString,
  branchId: uuidSchema,
  bankDataReference: z.object({
    customerReference: nonEmptyString,
    accountReference: nonEmptyString,
  }).strict().optional(),
}).strict();
export type BankImportLCCreateRequest = z.infer<typeof bankImportLCCreateRequestSchema>;

export const bankImportLCIntakeSourceSchema = z.object({
  id: uuidSchema,
  documentId: uuidSchema,
  filename: nonEmptyString,
  documentType: nonEmptyString,
  contentType: nonEmptyString,
  sizeBytes: z.number().int().nonnegative(),
  pageCount: z.number().int().positive(),
  sha256: nonEmptyString,
  objectKey: nonEmptyString,
  deduplicated: z.boolean(),
  createdAt: timestampSchema,
}).strict();
export type BankImportLCIntakeSource = z.infer<typeof bankImportLCIntakeSourceSchema>;

export const bankImportLCIntakeCaseSchema = z.object({
  id: uuidSchema,
  bankId: uuidSchema,
  branchId: uuidSchema,
  externalCaseRef: nonEmptyString,
  status: bankImportLCCaseStatusSchema,
  bankDataStatus: z.enum(['not_requested', 'resolved', 'unresolved']),
  bankDataFields: z.array(z.object({
    name: nonEmptyString,
    value: z.unknown(),
    sourceSystem: nonEmptyString,
    sourceRecordId: nonEmptyString,
    sourceVersion: nonEmptyString,
    retrievedAt: timestampSchema,
  }).strict()),
  bankDataFailures: z.array(z.object({
    service: nonEmptyString,
    code: nonEmptyString,
    resolved: z.boolean(),
  }).strict()),
  eligibility: z.boolean().nullable(),
  sources: z.array(bankImportLCIntakeSourceSchema),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
}).strict();
export type BankImportLCIntakeCase = z.infer<typeof bankImportLCIntakeCaseSchema>;

export const bankImportLCStartUploadRequestSchema = z.object({
  filename: nonEmptyString,
  contentType: nonEmptyString,
  documentType: nonEmptyString,
  expectedParts: z.number().int().min(1).max(10000),
  expectedSizeBytes: z.number().int().positive(),
}).strict();
export type BankImportLCStartUploadRequest = z.infer<typeof bankImportLCStartUploadRequestSchema>;

export const bankImportLCUploadManifestSchema = z.object({
  uploadId: uuidSchema,
  documentId: uuidSchema,
  state: z.enum(['open', 'consumed', 'expired', 'failed']),
  uploadedParts: z.array(z.number().int().positive()),
  expectedParts: z.number().int().positive(),
  expiresAt: timestampSchema,
}).strict();
export type BankImportLCUploadManifest = z.infer<typeof bankImportLCUploadManifestSchema>;

export const bankImportLCIntakeCompletionSchema = z.object({
  intakeSetVersion: z.number().int().positive(),
  intakeSetHash: z.string().length(64),
  sourceCount: z.number().int().nonnegative(),
  replayed: z.boolean(),
}).strict();
export type BankImportLCIntakeCompletion = z.infer<
  typeof bankImportLCIntakeCompletionSchema
>;

export const bankImportLCExtractionRequestedEventSchema = z.object({
  correlationId: nonEmptyString,
  intakeSetVersion: z.number().int().positive(),
  intakeSetHash: z.string().length(64),
}).strict();
export type BankImportLCExtractionRequestedEvent = z.infer<
  typeof bankImportLCExtractionRequestedEventSchema
>;

export const bankImportLCFieldPatchRequestSchema = z.object({
  jsonPointer: nonEmptyString.max(1024),
  value: jsonValueSchema,
  reason: z.string().trim().min(1).max(2000),
}).strict();
export type BankImportLCFieldPatchRequest = z.infer<
  typeof bankImportLCFieldPatchRequestSchema
>;

export const bankImportLCExtractionReviewCompleteRequestSchema = z.object({
  reason: z.string().trim().min(1).max(2000),
}).strict();
export type BankImportLCExtractionReviewCompleteRequest = z.infer<
  typeof bankImportLCExtractionReviewCompleteRequestSchema
>;

export const bankImportLCCanonicalFieldChangeSchema = z.object({
  jsonPointer: nonEmptyString.max(1024),
  before: jsonValueSchema,
  after: jsonValueSchema,
}).strict();
export type BankImportLCCanonicalFieldChange = z.infer<
  typeof bankImportLCCanonicalFieldChangeSchema
>;

export const bankImportLCVersionReviewSchema = z.object({
  id: uuidSchema,
  caseId: uuidSchema,
  parentVersionId: uuidSchema.nullable(),
  versionNumber: z.number().int().positive(),
  payloadHash: z.string().length(64),
  content: z.union([bankImportLCExtractionDraftSchema, bankImportLCContentSchema]),
  createdBy: uuidSchema,
  reason: nonEmptyString,
  changeSummary: nonEmptyString,
  createdAt: timestampSchema,
}).strict();
export type BankImportLCVersionReview = z.infer<
  typeof bankImportLCVersionReviewSchema
>;

export const bankImportLCVersionEditResponseSchema = z.object({
  version: bankImportLCVersionReviewSchema,
  created: z.boolean(),
  changes: z.array(bankImportLCCanonicalFieldChangeSchema),
}).strict();
export type BankImportLCVersionEditResponse = z.infer<
  typeof bankImportLCVersionEditResponseSchema
>;

export const bankImportLCVersionListResponseSchema = z.object({
  items: z.array(bankImportLCVersionReviewSchema),
}).strict();
export type BankImportLCVersionListResponse = z.infer<
  typeof bankImportLCVersionListResponseSchema
>;

export const bankImportLCVersionDiffSchema = z.object({
  fromVersionId: uuidSchema.nullable(),
  toVersionId: uuidSchema,
  changes: z.array(bankImportLCCanonicalFieldChangeSchema),
}).strict();
export type BankImportLCVersionDiff = z.infer<
  typeof bankImportLCVersionDiffSchema
>;

export const bankImportLCExtractionReviewCompleteResponseSchema = z.object({
  version: bankImportLCVersionReviewSchema,
  status: bankImportLCCaseStatusSchema,
}).strict();
export type BankImportLCExtractionReviewCompleteResponse = z.infer<
  typeof bankImportLCExtractionReviewCompleteResponseSchema
>;

export const bankImportLCCasePatchRequestSchema = z.object({
  title: nonEmptyString.optional(),
  priority: z.enum(['low', 'normal', 'high', 'urgent']).optional(),
  assignedOfficerIds: z.array(uuidSchema).optional(),
  content: bankImportLCContentSchema.optional(),
  reason: nonEmptyString,
}).strict();
export type BankImportLCCasePatchRequest = z.infer<typeof bankImportLCCasePatchRequestSchema>;

export const bankImportLCSubmitRequestSchema = z.object({
  versionId: uuidSchema,
  reason: nonEmptyString,
}).strict();
export type BankImportLCSubmitRequest = z.infer<typeof bankImportLCSubmitRequestSchema>;

export const bankImportLCDecideRequestSchema = z.object({
  decision: z.enum(['approved', 'returned', 'rejected']),
  reason: nonEmptyString,
  findingIds: z.array(uuidSchema).default([]),
}).strict();
export type BankImportLCDecideRequest = z.infer<typeof bankImportLCDecideRequestSchema>;

export const bankImportLCCompileRequestSchema = z.object({
  versionId: uuidSchema,
  messageType: z.enum(['MT700', 'MT701', 'MT707', 'examination_sheet', 'canonical_json']),
}).strict();
export type BankImportLCCompileRequest = z.infer<typeof bankImportLCCompileRequestSchema>;

export const bankImportLCHandoffRequestSchema = z.object({
  artifactId: uuidSchema,
  idempotencyKey: nonEmptyString,
}).strict();
export type BankImportLCHandoffRequest = z.infer<typeof bankImportLCHandoffRequestSchema>;

export const bankImportLCReconcileRequestSchema = z.object({
  bankSystemReference: nonEmptyString,
  status: z.enum(['accepted_by_bank_system', 'rejected_by_bank_system', 'reconciliation_required']),
  reason: nonEmptyString,
}).strict();
export type BankImportLCReconcileRequest = z.infer<typeof bankImportLCReconcileRequestSchema>;
